import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CaretRight, ChalkboardTeacher, Camera, Clock, ProhibitInset, UserList, Pill, ShieldCheck } from "@phosphor-icons/react";import { toast } from "sonner";
import { useAuthGate } from "@/lib/hooks/useAuthGate";
import SessionExpiredState from "@/components/auth/SessionExpiredState";
import {
  listChildrenInClassroom,
  listTeacherClassrooms,
  listReportsForChild,
} from "@/lib/dailyReports";
import {
  formatLocalTime,
  listClassroomNoticesForDate,
  NOTICE_LABELS,
} from "@/lib/attendanceNotices";
import { todayStr } from "@/lib/entryTypes";
import ChildAvatar from "@/components/children/ChildAvatar";
import ChildProfilePhotoEditor from "@/components/children/ChildProfilePhotoEditor";
import TeacherPickupSheet from "@/components/pickup/TeacherPickupSheet";
import TeacherMedicationsSheet from "@/components/medications/TeacherMedicationsSheet";
import TeacherSafetySheet from "@/components/health/TeacherSafetySheet";

export default function TeacherRoster() {
  const { status, profile } = useAuthGate();
  const [classrooms, setClassrooms] = useState([]);
  const [activeClassroomId, setActiveClassroomId] = useState(null);
  const [children, setChildren] = useState([]);
  const [reportSummaries, setReportSummaries] = useState({}); // childId -> entryCount
  const [noticesByChild, setNoticesByChild] = useState({}); // childId -> notice
  const [loading, setLoading] = useState(true);
  const [photoEditorChildId, setPhotoEditorChildId] = useState(null);
  const [pickupSheet, setPickupSheet] = useState({ open: false, child: null });
  const [medSheet, setMedSheet] = useState({ open: false, child: null });
  const [safetySheet, setSafetySheet] = useState({ open: false, child: null });

  const updateChildPhoto = (childId, newPath) => {
    setChildren((prev) =>
      prev.map((c) => (c.id === childId ? { ...c, profile_photo_path: newPath } : c)),
    );
  };

  // Load classrooms this teacher covers.
  useEffect(() => {
    if (status !== "ready") {
      // Auth still resolving OR session expired — release the loading
      // gate so the page can render its appropriate state instead of
      // hanging on the spinner forever.
      if (status === "session_expired") setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const rows = await listTeacherClassrooms(profile.id);
        setClassrooms(rows);
        setActiveClassroomId(rows[0]?.id || null);
      } catch (err) {
        toast.error(err.message || "Could not load your classrooms");
      } finally {
        setLoading(false);
      }
    })();
  }, [status, profile?.id]);

  // Load children for active classroom.
  useEffect(() => {
    if (!activeClassroomId) return;
    (async () => {
      try {
        const kids = await listChildrenInClassroom(activeClassroomId);
        setChildren(kids);
        const today = todayStr();
        const summaries = {};
        await Promise.all(
          kids.map(async (c) => {
            const reports = await listReportsForChild(c.id, 1);
            const todays = reports.find((r) => r.report_date === today);
            summaries[c.id] = {
              loggedToday: Boolean(todays),
              entryCount: todays?.entries?.length ?? 0,
            };
          }),
        );
        setReportSummaries(summaries);
        // Load any submitted absence/late notices for today, group by child.
        try {
          const notices = await listClassroomNoticesForDate({
            classroomId: activeClassroomId,
            date: today,
          });
          const map = {};
          notices.forEach((n) => {
            map[n.child_id] = n;
          });
          setNoticesByChild(map);
        } catch {
          setNoticesByChild({});
        }
      } catch (err) {
        toast.error(err.message || "Could not load roster");
      }
    })();
  }, [activeClassroomId]);

  if (status === "session_expired") {
    return <SessionExpiredState scope="roster" testId="teacher-roster-session-expired" />;
  }

  if (loading) {
    return (
      <div
        data-testid="teacher-roster-loading"
        className="flex items-center justify-center py-16"
      >
        <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  if (classrooms.length === 0) {
    return (
      <div
        data-testid="teacher-roster-empty-classrooms"
        className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center text-neutral-500"
      >
        You aren't assigned to any classrooms yet. Please contact your admin.
      </div>
    );
  }

  return (
    <div data-testid="teacher-roster" className="space-y-6">
      <section>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Roster · {todayStr()}
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900">
          Classroom
        </h1>
      </section>

      {classrooms.length > 1 && (
        <div
          data-testid="classroom-picker"
          className="flex gap-2 overflow-x-auto pb-1"
        >
          {classrooms.map((c) => (
            <button
              key={c.id}
              data-testid={`classroom-tab-${c.id}`}
              onClick={() => setActiveClassroomId(c.id)}
              className={`flex-shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                activeClassroomId === c.id
                  ? "border-primary bg-primary/10 text-primary-hover"
                  : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}

      {classrooms.length === 1 && (
        <div className="flex items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
            <ChalkboardTeacher size={20} weight="duotone" className="text-primary-hover" />
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
              Classroom
            </p>
            <p className="font-heading text-base font-semibold tracking-tight text-neutral-900">
              {classrooms[0].name}
              {classrooms[0].age_group ? (
                <span className="ml-2 text-sm font-normal text-neutral-500">
                  · {classrooms[0].age_group}
                </span>
              ) : null}
            </p>
          </div>
        </div>
      )}

      <ul className="space-y-2" data-testid="roster-children">
        {children.length === 0 ? (
          <li className="rounded-2xl border border-dashed border-neutral-200 bg-white p-6 text-center text-sm text-neutral-500">
            No children in this classroom yet.
          </li>
        ) : (
          children.map((child) => {
            const summary = reportSummaries[child.id] || {};
            const photoOpen = photoEditorChildId === child.id;
            const notice = noticesByChild[child.id];
            return (
              <li key={child.id}>
                <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-4 transition-all hover:-translate-y-[1px] hover:shadow-sm">
                  <Link
                    to={`/teacher/reports/${child.id}`}
                    data-testid={`roster-child-${child.id}`}
                    className="order-1 flex min-w-0 basis-full items-center gap-3 sm:order-none sm:flex-1 sm:basis-auto"
                  >
                    <ChildAvatar
                      path={child.profile_photo_path}
                      name={`${child.first_name || ""} ${child.last_name || ""}`}
                      size={44}
                      testId={`roster-avatar-${child.id}`}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="font-heading text-base font-semibold tracking-tight text-neutral-900">
                        {child.first_name} {child.last_name || ""}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {summary.loggedToday
                          ? `${summary.entryCount} entr${summary.entryCount === 1 ? "y" : "ies"} today`
                          : "No report yet today"}
                      </p>
                    </div>
                    {notice && (
                      <span
                        data-testid={`roster-absence-${child.id}`}
                        data-notice-type={notice.notice_type}
                        title={
                          notice.reason ||
                          NOTICE_LABELS[notice.notice_type]
                        }
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${
                          notice.notice_type === "absent"
                            ? "bg-[#C6851E]/10 text-[#8A5A12]"
                            : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {notice.notice_type === "absent" ? (
                          <ProhibitInset size={10} weight="duotone" />
                        ) : (
                          <Clock size={10} weight="duotone" />
                        )}
                        {notice.notice_type === "absent"
                          ? "Absent"
                          : notice.expected_arrival_time
                            ? `Late · ${formatLocalTime(notice.expected_arrival_time)}`
                            : "Late"}
                      </span>
                    )}
                    {child.allergies && (
                      <span
                        data-testid={`roster-allergy-${child.id}`}
                        className="rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-primary-hover"
                      >
                        Allergy
                      </span>
                    )}
                  </Link>
                  <div className="order-2 flex w-full flex-wrap justify-end gap-2 sm:order-none sm:w-auto">
                    <button
                      type="button"
                      data-testid={`roster-pickup-${child.id}`}
                      onClick={() => setPickupSheet({ open: true, child })}
                      className="flex h-9 items-center justify-center rounded-full border border-neutral-200 bg-white px-3 text-[11px] font-semibold uppercase tracking-wider text-neutral-700 transition-all hover:bg-neutral-50"
                      title="Authorised pickup"
                      aria-label="Authorised pickup list"
                    >
                      <UserList size={12} weight="duotone" />
                    </button>
                    <button
                      type="button"
                      data-testid={`roster-medications-${child.id}`}
                      onClick={() => setMedSheet({ open: true, child })}
                      className="flex h-9 items-center justify-center rounded-full border border-neutral-200 bg-white px-3 text-[11px] font-semibold uppercase tracking-wider text-neutral-700 transition-all hover:bg-neutral-50"
                      title="Medications"
                      aria-label="Medications"
                    >
                      <Pill size={12} weight="duotone" />
                    </button>
                    <button
                      type="button"
                      data-testid={`roster-safety-${child.id}`}
                      onClick={() => setSafetySheet({ open: true, child })}
                      className="flex h-9 items-center justify-center rounded-full border border-primary/30 bg-primary/5 px-3 text-[11px] font-semibold uppercase tracking-wider text-primary-hover transition-all hover:bg-primary/10"
                      title="Safety guidance"
                      aria-label="Safety guidance"
                    >
                      <ShieldCheck size={12} weight="duotone" />
                      <span>Safety</span>
                    </button>
                    <button
                      type="button"
                      data-testid={`roster-photo-toggle-${child.id}`}
                      onClick={() =>
                        setPhotoEditorChildId(photoOpen ? null : child.id)
                      }
                      className="flex h-9 items-center justify-center rounded-full border border-neutral-200 bg-white px-3 text-[11px] font-semibold uppercase tracking-wider text-neutral-700 transition-all hover:bg-neutral-50"
                      title="Profile photo"
                      aria-label="Edit profile photo"
                    >
                      <Camera size={12} weight="duotone" />
                    </button>
                    <Link
                      to={`/teacher/incidents/new?childId=${child.id}`}
                      data-testid={`roster-report-incident-${child.id}`}
                      className="flex h-9 items-center justify-center rounded-full bg-neutral-100 px-3 text-[11px] font-semibold uppercase tracking-wider text-neutral-700 transition-all hover:bg-neutral-200"
                      title="Report an incident"
                    >
                      Incident
                    </Link>
                  </div>
                </div>
                {photoOpen && (
                  <div
                    data-testid={`roster-photo-editor-${child.id}`}
                    className="mt-1 rounded-2xl border border-primary/20 bg-primary/5 p-4"
                  >
                    <ChildProfilePhotoEditor
                      child={child}
                      onChanged={(newPath) => updateChildPhoto(child.id, newPath)}
                    />
                  </div>
                )}
              </li>
            );
          })
        )}
      </ul>
      <TeacherPickupSheet
        open={pickupSheet.open}
        onOpenChange={(o) => setPickupSheet((s) => ({ ...s, open: o }))}
        child={pickupSheet.child}
      />
      <TeacherMedicationsSheet
        open={medSheet.open}
        onOpenChange={(o) => setMedSheet((s) => ({ ...s, open: o }))}
        child={medSheet.child}
        administeredBy={profile?.id}
      />
      <TeacherSafetySheet
        open={safetySheet.open}
        onOpenChange={(open) => setSafetySheet((state) => ({ ...state, open }))}
        child={safetySheet.child}
      />
    </div>
  );
}
